import {
    ActionReducerMapBuilder,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import {
    IInvent,
    IReportRestByDay,
    IReportsState,
    IPosition,
    TransformTopResult,
    IReportCountChecksByDay,
    IReportAverageCheckByDay,
    IReportAbc,
    ILike4Like,
    ISourcesData,
} from '../types';
import { RootState } from '@config/store';
import {
    geReportAbc,
    getSources,
    getAverageCheckByDay,
    getCountChecksByDay,
    getLike4Like,
    getRevenueRestaurantsByDay,
    getStopPositions,
    getTopPositions,
} from '@api/thunks/reports';
import { linesColorArray } from '@config/colors';
import { colors } from '@styles';

const initialState: IReportsState = {
    reportsRestByDay: null,
    countChecksByDay: null,
    averageCheckByDay: null,
    sources: null,
    inventedCategories: [],
    categoriesNames: [],
    topPositions: [],
    filteredTopPositions: [],
    stopPositions: [],
    filteredStopPositions: [],
    like4like: null,
    reportAbc: null,
    loading: 'idle',
    error: null,
};

const reportsSlice = createSlice({
    name: '@@reports',
    initialState,
    reducers: {
        setFilteredTop: (
            state: IReportsState,
            { payload }: PayloadAction<(IPosition & IInvent)[]>
        ) => {
            state.filteredTopPositions = payload;
        },
        clearReportsState: () => {
            return initialState;
        },
    },
    extraReducers: (builder: ActionReducerMapBuilder<IReportsState>) => {
        builder
            .addCase(
                getRevenueRestaurantsByDay.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<IReportRestByDay>
                ) => {
                    state.reportsRestByDay = payload;
                }
            )
            .addCase(
                getCountChecksByDay.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<IReportCountChecksByDay>
                ) => {
                    state.countChecksByDay = payload;
                }
            )
            .addCase(
                getAverageCheckByDay.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<IReportAverageCheckByDay>
                ) => {
                    state.averageCheckByDay = payload;
                }
            )
            .addCase(
                getLike4Like.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<ILike4Like>
                ) => {
                    state.like4like = payload;
                }
            )
            .addCase(
                getSources.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<ISourcesData>
                ) => {
                    state.sources = payload;
                }
            )

            .addCase(
                geReportAbc.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<IReportAbc>
                ) => {
                    const { chart, ...rest } = payload;
                    const chartData = chart.map((item, i) => ({
                        range: item.range,
                        value: item.value,
                        color:
                            linesColorArray[i]?.backgroundColor ||
                            colors.defChart,
                    }));
                    state.reportAbc = { ...rest, chart: chartData };
                }
            )

            .addCase(
                getTopPositions.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<TransformTopResult>
                ) => {
                    const {
                        updatedData,
                        inventedCategoriesNameArray,
                        categoriesNameArray,
                    } = payload;
                    state.topPositions = updatedData;
                    state.filteredTopPositions = updatedData;
                    state.inventedCategories = inventedCategoriesNameArray;
                    state.categoriesNames = categoriesNameArray;
                }
            )
            .addCase(
                getStopPositions.fulfilled,
                (
                    state: IReportsState,
                    { payload }: PayloadAction<TransformTopResult>
                ) => {
                    const {
                        updatedData,
                        inventedCategoriesNameArray,
                        categoriesNameArray,
                    } = payload;
                    state.stopPositions = updatedData;
                    state.filteredStopPositions = updatedData;
                    state.inventedCategories = inventedCategoriesNameArray;
                    state.categoriesNames = categoriesNameArray;
                }
            )

            .addMatcher(
                action => action.type.endsWith('/pending'),
                state => {
                    state.loading = 'loading';
                    state.error = null;
                }
            )
            .addMatcher(
                action => action.type.endsWith('/rejected'),
                (state, action: PayloadAction<string>) => {
                    state.loading = 'idle';
                    state.error = action.payload;
                }
            )
            .addMatcher(
                action => action.type.endsWith('/fulfilled'),
                state => {
                    state.loading = 'idle';
                }
            );
    },
});

export const { setFilteredTop, clearReportsState } = reportsSlice.actions;
export const reportsReducer = reportsSlice.reducer;

export const reportsSelect = (state: RootState) => state.reports;
